import { clsx } from 'clsx';

import { SvgCheveronLeft, SvgCheveronRight } from '@/components/icons';
import { useBudgetStore } from '@/store/budget';
import { addMonthLength, shortFromMonth, yearFromMonth } from '@/utils/months';

const STRIDE = 6;

/**
 * The month strip at the top of the budget page: a year row, then one button
 * per month. Months currently visible in the grid get the purple pill, exactly
 * as in the demo screenshot.
 */
export function MonthPicker() {
  const startMonth = useBudgetStore(state => state.startMonth);
  const monthCount = useBudgetStore(state => state.monthCount);
  const bounds = useBudgetStore(state => state.bounds);
  const shiftMonth = useBudgetStore(state => state.shiftMonth);
  const jumpToMonth = useBudgetStore(state => state.jumpToMonth);

  const months: string[] = [];
  for (let offset = -STRIDE; offset < monthCount + STRIDE; offset += 1) {
    months.push(addMonthLength(startMonth, offset));
  }

  const visible = months.slice(STRIDE, STRIDE + monthCount);
  const canGoBack = !bounds || addMonthLength(startMonth, -1) >= bounds.min;
  const canGoForward =
    !bounds || addMonthLength(visible[visible.length - 1], 1) <= bounds.max;

  let lastYear = 0;

  return (
    <div className="flex items-center gap-1" data-testid="month-picker">
      <button
        type="button"
        disabled={!canGoBack}
        onClick={() => shiftMonth(-1)}
        aria-label="上一月"
        className="rounded-sm p-1 text-page-text-light hover:bg-button-bare-background-hover disabled:opacity-40"
      >
        <SvgCheveronLeft width={14} height={14} />
      </button>

      <div className="flex items-end">
        {months.map(month => {
          const year = yearFromMonth(month);
          const showYear = year !== lastYear;
          lastYear = year;
          const isActive = visible.includes(month);
          return (
            <div key={month} className="flex flex-col items-center">
              <span className="h-4 text-xs text-page-text-light">
                {showYear ? year : ''}
              </span>
              <button
                type="button"
                onClick={() => jumpToMonth(month)}
                className={clsx(
                  'rounded-sm px-1.5 py-0.5 text-sm',
                  isActive
                    ? 'bg-button-primary-background font-medium text-button-primary-text hover:bg-button-primary-background-hover'
                    : 'text-page-text hover:bg-button-bare-background-hover',
                )}
              >
                {shortFromMonth(month)}
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        disabled={!canGoForward}
        onClick={() => shiftMonth(1)}
        aria-label="下一月"
        className="rounded-sm p-1 text-page-text-light hover:bg-button-bare-background-hover disabled:opacity-40"
      >
        <SvgCheveronRight width={14} height={14} />
      </button>
    </div>
  );
}
